import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle, Car, Users, Package, DollarSign } from 'lucide-react';

interface DeleteVehicleCategoryConfirmationProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vehicleCategoryDetails: string;
  vehicleCategoryId: number;
  onConfirm: (vehicleCategoryId: number) => void;
  isLoading?: boolean;
}

const DeleteVehicleCategoryConfirmation: React.FC<DeleteVehicleCategoryConfirmationProps> = ({
  open,
  onOpenChange,
  vehicleCategoryDetails,
  vehicleCategoryId,
  onConfirm,
  isLoading = false,
}) => {
  const handleConfirm = () => {
    onConfirm(vehicleCategoryId);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <AlertDialogTitle>Delete Vehicle Category</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-left">
            Are you sure you want to delete the vehicle category <span className="font-semibold">{vehicleCategoryDetails}</span>?
            This action cannot be undone and will permanently remove:
            <ul className="mt-2 ml-4 list-disc space-y-1 text-sm">
              <li>Vehicle category configuration and settings</li>
              <li>Pricing rules and fare calculations</li>
              <li>Passenger and luggage capacity settings</li>
              <li>Cancellation fee and wait time settings</li>
              <li>Associated pricing rules and surge configurations</li>
            </ul>
            <div className="mt-3 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-sm text-destructive font-medium">
                <strong>Warning:</strong> This action is permanent and cannot be reversed.
                All related pricing and promotion configurations will be affected.
              </p>
            </div>
            <div className="mt-3 flex items-center gap-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <div className="flex items-center gap-2 text-amber-700">
                <Car className="w-4 h-4" />
                <Users className="w-4 h-4" />
                <Package className="w-4 h-4" />
                <DollarSign className="w-4 h-4" />
              </div>
              <p className="text-sm text-amber-800">
                <strong>Impact:</strong> Riders will no longer be able to select this vehicle type.
                Existing bookings may need vehicle reassignment.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isLoading}
            className="bg-destructive hover:bg-destructive/90"
          >
            {isLoading ? 'Deleting...' : 'Delete Vehicle Category'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteVehicleCategoryConfirmation;